import {
  Modal,
  SideModal,
  createEventData,
  pushToDataLayer,
  pushToDataLayerRespondentCreateSuccess,
  useToast,
} from '@arnold/common';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GraphQLErrorCard } from '../../components';
import RespondentForm, { RespondentFormStatus } from '../../components/Forms/RespondentForm/RespondentForm';
import {
  ContactSource,
  ContactType,
  CreateRespondentMutation,
  GetCompletedSurveysQuery,
  OrganizationQuery,
  ProcessStatus,
  SurveyQuery,
  SurveyStatus,
  TopicGroupTypeCode,
  UpdateRespondentMutation,
  UpdateRespondentMutationFn,
  UserQuery,
  useCreateRespondentMutation,
  useGetCompletedSurveysQuery,
  useOrganizationSettingQuery,
  useReportRecipientsQuery,
  useSurveysQuery,
  useUpdateRespondentMutation,
} from '../../generated/hooks';
import {
  getOrganizationStructure,
  getOrganizationTeams,
  getReportRecipients,
  getSurveyGroups,
} from '../../graphql/queries';
import { getContact, getRawContact } from '../../lib/common';
import i18n from '../../translations/i18n';
import { useGetOrganizationTeams, useGetSurveyGroups } from '../Surveys/utils';
import { MemberWithLeaderOfTeams } from './TeamsAndPeople';
import ee from '../../lib/eventEmitter';

interface IProps {
  user: UserQuery['user'];
  organization: NonNullable<OrganizationQuery['organization']>;
  // when respondent is null, it means we are creating a new one
  respondent: MemberWithLeaderOfTeams | null;
  defaultTeamId?: string;
  onClose: () => void;
  setLatestCreatedEmployeeId: (id: string) => void;
  onCreateTeamSelected: (externallySetTeamId: (teamId: string) => void) => void;
  latestCreatedEmployeeId?: string;
  wide?: boolean;
  disableCreatingTeam?: boolean;
}

const filterSurveysWithNewReportTeam = (
  surveys: GetCompletedSurveysQuery['getCompletedSurveys'],
  updatedReportTeamIds: (string | null)[],
  respondentId: string,
) => {
  return surveys?.filter((survey) => {
    const report = survey.reports?.find((report) => report.respondent?.id === respondentId);
    const allReports = updatedReportTeamIds.some((id) => id === null);
    if (!report && (allReports || updatedReportTeamIds?.some((id) => survey.teamIds?.includes(id as string)))) {
      return true;
    }
    return false;
  });
};

export const EmployeeDetail = ({
  organization,
  respondent,
  defaultTeamId,
  user,
  onClose,
  setLatestCreatedEmployeeId,
  latestCreatedEmployeeId,
  wide,
  onCreateTeamSelected,
  disableCreatingTeam,
}: IProps) => {
  const { t } = useTranslation('updateRespondent');
  const { addToast } = useToast();
  const [opened, setOpened] = useState(true);
  const [formStatus, setFormStatus] = useState<RespondentFormStatus>();
  const [saveChangesModalShow, setSaveChangesModalShow] = useState(false);
  const [updatedReportTeamIds, setUpdatedReportTeamIds] = useState<string[]>([]);

  const onCloseClicked = () => {
    if (formStatus?.somethingChanged) {
      setSaveChangesModalShow(true);
    } else {
      setOpened(false);
    }
  };

  const onEmployeeDeleted = () => {
    setOpened(false);
  };

  useEffect(() => {
    setOpened(true);
  }, [respondent?.id]);

  const {
    data: organizationSettings,
    loading: organizationSettingsLoading,
    error: organizationSettingsError,
  } = useOrganizationSettingQuery({
    variables: { organizationId: organization.id },
  });

  const { organizationTeams, loading: teamsLoading, error: teamsError } = useGetOrganizationTeams(organization.id);

  const {
    data: reportRecipientsData,
    loading: reportRecipientsLoading,
    error: reportRecipientsError,
  } = useReportRecipientsQuery({
    variables: {
      organizationId: organization.id,
    },
  });

  const {
    data: surveysData,
    loading: surveysLoading,
    error: surveysError,
  } = useSurveysQuery({
    variables: {
      languageCode: i18n.language,
    },
    skip: !!respondent,
  });
  // surveys and surveyGroups fetching can be skipped when updating an existing respondent
  const { surveyGroups, loading: sgLoading, error: sgError } = useGetSurveyGroups({ skip: !!respondent });
  const { data: completedSurveysData, loading: completedSurveysLoading } = useGetCompletedSurveysQuery({
    variables: { organizationId: organization.id },
  });

  const [updateRespondentMutation, { loading: updateMutationloading, error: updateMutationError }] =
    useUpdateRespondentMutation({
      onCompleted: (data: UpdateRespondentMutation) => {
        addToast(
          t('updateSuccess', {
            respondentName: `${data.updateRespondent.firstname} ${data.updateRespondent.surname}`,
          }),
        );
        setOpened(false);
        const filteredSurveys = filterSurveysWithNewReportTeam(
          completedSurveysData?.getCompletedSurveys || [],
          updatedReportTeamIds,
          data.updateRespondent.id,
        );

        if (filteredSurveys && filteredSurveys?.length > 0) {
          ee.emitSendNewReports(
            filteredSurveys! as unknown as SurveyQuery['survey'][],
            data.updateRespondent.id,
            `${data.updateRespondent.firstname} ${data.updateRespondent.surname}`,
          );
        }
      },
      onError: () => {
        setOpened(false);
        pushToDataLayer({
          userId: user!.id,
          orgId: organization.id,
          event: 'ux.respondent-update-error',
          ...createEventData('organization', 'updateRespondentError', 'organization update respondent error'),
        });
      },
      refetchQueries: [getSurveyGroups, getOrganizationTeams, getReportRecipients],
      awaitRefetchQueries: true,
    });

  const [createRespondentMutation, { loading: createMutationLoading, error: createMutationError }] =
    useCreateRespondentMutation({
      onCompleted: (data: CreateRespondentMutation) => {
        addToast(
          t('addRespondent:createSuccess', {
            respondentName: `${data.createRespondent.firstname} ${data.createRespondent.surname}`,
          }),
        );
        pushToDataLayerRespondentCreateSuccess(user);
        setOpened(false);
        setLatestCreatedEmployeeId(data.createRespondent.id);
        const filteredSurveys = filterSurveysWithNewReportTeam(
          completedSurveysData?.getCompletedSurveys || [],
          updatedReportTeamIds,
          data.createRespondent.id,
        );

        if (filteredSurveys && filteredSurveys?.length > 0) {
          ee.emitSendNewReports(
            filteredSurveys! as unknown as SurveyQuery['survey'][],
            data.createRespondent.id,
            `${data.createRespondent.firstname} ${data.createRespondent.surname}`,
          );
        }
      },
      onError: () => {
        setOpened(false);
        pushToDataLayer({
          userId: user?.id,
          orgId: organization?.id,
          event: 'ux.respondent-create-error',
          ...createEventData('organization', 'createRespondentError', 'organization create respondent error'),
        });
      },
      refetchQueries: [
        {
          query: getOrganizationStructure,
          variables: {
            organizationId: organization.id,
          },
        },
        getSurveyGroups,
        {
          query: getOrganizationTeams,
          variables: {
            organizationId: organization.id,
          },
        },
        getReportRecipients,
      ],
      awaitRefetchQueries: true,
    });

  const workEmail = respondent ? getContact(respondent, ContactType.Email, ContactSource.Primary) : undefined;
  const personalEmail = respondent ? getContact(respondent, ContactType.Email, ContactSource.Respondent) : undefined;
  const phone = respondent ? getRawContact(respondent, ContactType.Sms, ContactSource.Respondent) : undefined;

  const team = respondent
    ? organizationTeams?.find((t) => {
        return t.members.some((member) => member.id === respondent.id);
      })
    : defaultTeamId
      ? organizationTeams?.find((t) => t.id === defaultTeamId)
      : undefined;

  // onboardings, process surveys and surveys are only relevant when creating a new respondent
  const onboardings = respondent
    ? undefined
    : surveyGroups?.filter(
        (sg) =>
          sg.topicGroup.typeCode === TopicGroupTypeCode.Onboarding &&
          [ProcessStatus.Completed, ProcessStatus.Ongoing, ProcessStatus.Prepared].includes(sg.status),
      );

  const processSurveys = respondent
    ? undefined
    : surveyGroups?.filter(
        (sg) =>
          sg.topicGroup.typeCode !== TopicGroupTypeCode.Onboarding &&
          [ProcessStatus.Completed, ProcessStatus.Ongoing, ProcessStatus.Prepared].includes(sg.status),
      );

  const surveys = respondent
    ? undefined
    : surveysData?.user.organization?.surveys
        .filter((survey) => [SurveyStatus.Ongoing, SurveyStatus.Prepared].includes(survey.status) && !survey.public)
        .sort((a, b) => (a.sendAt > b.sendAt ? 1 : -1)) || [];

  const error =
    organizationSettingsError ||
    teamsError ||
    surveysError ||
    sgError ||
    updateMutationError ||
    createMutationError ||
    reportRecipientsError;

  const loading =
    organizationSettingsLoading ||
    !organizationSettings ||
    (respondent && teamsLoading) ||
    (!respondent && surveysLoading) ||
    (!respondent && sgLoading) ||
    updateMutationloading ||
    createMutationLoading ||
    reportRecipientsLoading ||
    completedSurveysLoading;

  return (
    <>
      <SideModal
        opened={opened}
        onCloseAction={onCloseClicked}
        onClosedAnimationComplete={onClose}
        primaryButton={{ label: t('modal:save'), onClick: formStatus?.save }}
        secondaryButton={{ label: t('modal:cancel'), onClick: onCloseClicked }}
        loadingOverlay={loading}
        wide={wide}
        adminZIndex={true}
      >
        <>
          {error ? (
            <GraphQLErrorCard />
          ) : (
            !loading && (
              <RespondentForm
                respondent={respondent ?? undefined}
                organization={organization}
                organizationTeams={organizationTeams || []}
                saveRespondent={
                  !formStatus?.somethingChanged
                    ? (onCloseClicked as UpdateRespondentMutationFn)
                    : respondent
                      ? updateRespondentMutation
                      : createRespondentMutation
                }
                workEmail={workEmail}
                personalEmail={personalEmail}
                phone={phone?.phoneNumber?.nationalNumber || ''}
                countryCallingCode={
                  phone?.phoneNumber?.countryCallingCode ||
                  organizationSettings?.organization?.config?.countryCallingCode
                }
                team={team}
                onboardings={onboardings}
                processSurveys={processSurveys}
                surveys={surveys}
                reportRecipients={reportRecipientsData?.reportRecipients}
                formStatus={formStatus}
                setFormStatus={setFormStatus}
                onDeletionComplete={onEmployeeDeleted}
                setUpdatedReportTeamIds={setUpdatedReportTeamIds}
                onCreateTeamSelected={onCreateTeamSelected}
                disableCreatingTeam={disableCreatingTeam}
              />
            )
          )}
        </>
      </SideModal>
      <Modal
        show={saveChangesModalShow}
        onSubmit={formStatus?.save}
        onCancel={() => setOpened(false)}
        onHide={() => setSaveChangesModalShow(false)}
        title={t('saveChangesModalTitle')}
        text={t('saveChangesModalText')}
        buttons={{
          submit: { title: t('saveChangesModalSubmit') },
          cancel: { title: t('saveChangesModalCancel') },
        }}
      />
    </>
  );
};
